import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TaskFormContent = ({ task, onChange }) => {
  const [formData, setFormData] = useState(task || {
    title: '',
    notes: '',
    dueDate: '',
    dueTime: '',
    estimatedTime: '',
    tags: [],
    isUrgent: false,
    isImportant: false,
    recurrence: 'none',
  });

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onChange(updated);
  };

  const handleTagInput = (e) => {
    if (e?.key === 'Enter' && e?.target?.value?.trim()) {
      e?.preventDefault();
      const newTag = e?.target?.value?.trim();
      if (!formData?.tags?.includes(newTag)) {
        handleChange('tags', [...formData?.tags, newTag]);
      }
      e.target.value = '';
    }
  };

  const removeTag = (tagToRemove) => {
    handleChange('tags', formData?.tags?.filter(tag => tag !== tagToRemove));
  };

  const recurrenceOptions = [
    { value: 'none', label: 'No Repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  return (
    <div className="space-y-4 md:space-y-5">
      {/* Title */}
      <Input
        label="Task Title"
        type="text"
        placeholder="Enter task title"
        value={formData?.title}
        onChange={(e) => handleChange('title', e?.target?.value)}
        required
      />
      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Notes
        </label>
        <textarea
          value={formData?.notes}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          placeholder="Add task notes or description..."
          rows={4}
          className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none transition-all"
        />
      </div>
      {/* Due Date & Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Input
          label="Due Date"
          type="date"
          value={formData?.dueDate}
          onChange={(e) => handleChange('dueDate', e?.target?.value)}
          required
        />

        <Input
          label="Due Time"
          type="time"
          value={formData?.dueTime}
          onChange={(e) => handleChange('dueTime', e?.target?.value)}
        />
      </div>
      {/* Estimated Time */}
      <Input
        label="Estimated Time"
        type="text"
        placeholder="e.g., 2h 30m"
        value={formData?.estimatedTime}
        onChange={(e) => handleChange('estimatedTime', e?.target?.value)}
        description="How long will this task take?"
      />
      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Tags
        </label>
        <input
          type="text"
          placeholder="Type and press Enter to add tags"
          onKeyDown={handleTagInput}
          className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
        />
        {formData?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData?.tags?.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${tag} tag`}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      {/* Priority Toggles */}
      <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
        <p className="text-sm font-medium text-foreground mb-3">
          Task Priority
        </p>
        <Checkbox
          label="Urgent"
          description="Requires immediate attention"
          checked={formData?.isUrgent}
          onChange={(e) => handleChange('isUrgent', e?.target?.checked)}
        />
        <Checkbox
          label="Important"
          description="Contributes to long-term goals"
          checked={formData?.isImportant}
          onChange={(e) => handleChange('isImportant', e?.target?.checked)}
        />
      </div>
      {/* Recurrence */}
      <Select
        label="Repeat"
        options={recurrenceOptions}
        value={formData?.recurrence}
        onChange={(value) => handleChange('recurrence', value)}
      />
      {/* Quadrant Preview */}
      <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
        <p className="text-sm font-medium text-foreground mb-1">
          Will be added to:
        </p>
        <p className="text-sm text-muted-foreground">
          {formData?.isUrgent && formData?.isImportant && 'Urgent & Important (Do First)'}
          {!formData?.isUrgent && formData?.isImportant && 'Not Urgent & Important (Schedule)'}
          {formData?.isUrgent && !formData?.isImportant && 'Urgent & Not Important (Delegate)'}
          {!formData?.isUrgent && !formData?.isImportant && 'Not Urgent & Not Important (Eliminate)'}
        </p>
      </div>
    </div>
  );
};

export default TaskFormContent;